import { useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { KeyRound, LogOut, ChevronUp } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface UserMenuProps {
  isCollapsed: boolean;
  onOpenChangePassword: () => void;
}

interface CollapsedMenuPortalProps {
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  /** Icon-only layout for narrow collapsed sidebar */
  iconOnly: boolean;
  children: React.ReactNode;
}

/**
 * Renders the collapsed-sidebar menu in a portal with fixed positioning so it is not
 * clipped by the dashboard `overflow-hidden` or the narrow sidebar width.
 */
function CollapsedUserMenuPortal({
  onClose,
  anchorRef,
  iconOnly,
  children,
}: CollapsedMenuPortalProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchorRef.current) return;

    function updatePosition() {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const gap = 4;
      setPos({
        top: rect.top - gap,
        left: rect.left + rect.width / 2,
      });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef]);

  if (pos === null) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="menu"
        className={cn(
          "fixed z-[110] overflow-hidden rounded-lg border border-white/10 bg-slate-800 shadow-lg",
          iconOnly
            ? "flex flex-col gap-0.5 p-1"
            : "min-w-[220px]"
        )}
        style={{
          top: pos.top,
          left: pos.left,
          transform: "translate(-50%, -100%)",
        }}
      >
        {children}
      </div>
    </>,
    document.body
  );
}

export function UserMenu({ isCollapsed, onOpenChangePassword }: UserMenuProps) {
  const { user, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const userLabel = user?.fullName || user?.name || user?.email || "User";
  const initials = userLabel
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const expandedMenuItems = (
    <>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          setIsOpen(false);
          onOpenChangePassword();
        }}
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
      >
        <KeyRound className="h-4 w-4 shrink-0" />
        <span>Change password</span>
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          setIsOpen(false);
          void signOut();
        }}
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
      >
        <LogOut className="h-4 w-4 shrink-0" />
        <span>Sign out</span>
      </button>
    </>
  );

  const collapsedMenuItems = (
    <>
      <IconButton
        type="button"
        role="menuitem"
        title="Change password"
        aria-label="Change password"
        onClick={() => {
          setIsOpen(false);
          onOpenChangePassword();
        }}
      >
        <KeyRound aria-hidden />
      </IconButton>
      <IconButton
        type="button"
        role="menuitem"
        title="Sign out"
        aria-label="Sign out"
        onClick={() => {
          setIsOpen(false);
          void signOut();
        }}
      >
        <LogOut aria-hidden />
      </IconButton>
    </>
  );

  return (
    <div
      className={cn(
        "relative",
        isCollapsed && "flex w-full flex-col items-center"
      )}
    >
      {isCollapsed ? (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-pink-600 text-xs font-semibold text-white transition-transform hover:scale-105"
          title={userLabel}
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          {initials}
        </button>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white/10"
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-pink-600 text-xs font-semibold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{userLabel}</p>
            <p className="truncate text-xs text-slate-400">{user?.email}</p>
          </div>
          <ChevronUp
            className={cn(
              "h-4 w-4 shrink-0 text-slate-400 transition-transform",
              isOpen && "rotate-180"
            )}
          />
        </button>
      )}

      {isOpen && !isCollapsed ? (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-hidden
          />
          <div
            className="absolute bottom-full left-0 right-0 z-20 mb-1 overflow-hidden rounded-lg border border-white/10 bg-slate-800 shadow-lg"
            role="menu"
          >
            {expandedMenuItems}
          </div>
        </>
      ) : null}

      {isCollapsed && isOpen ? (
        <CollapsedUserMenuPortal
          onClose={() => setIsOpen(false)}
          anchorRef={triggerRef}
          iconOnly
        >
          {collapsedMenuItems}
        </CollapsedUserMenuPortal>
      ) : null}
    </div>
  );
}
